/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useMemo } from "react";
import primaryComponents from "../../../../primaryComponents";
import secondaryComponents from "../../..";
import { usePricingForm } from "./usePricingForm";
import globalStyles from "../../../../../assets/styles/base/globalPage.module.scss";
import styles from "./styles.module.scss";
import { DeleteIcon } from "../../../../../assets/svgIcons";
import { getDefaultStoreProperties } from "../../../../../helpers";

export const PricingForm = ({
  formData = {},
  validation = {},
  isEditMode = false,
}) => {
  const { rootFormDetails, setRootFormDetails } = formData;
  const { isLoading, pricingTypes, storeList } = usePricingForm();
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = validation;

  const storeProperties = rootFormDetails?.store_properties ?? [];

  /* ---------------------------------- utils --------------------------------- */

  const storeMap = useMemo(() => {
    return storeList?.reduce((acc, store) => {
      acc[`${store.id}`] = store;
      return acc;
    }, {});
  }, [storeList, rootFormDetails?.store_properties]);

  const getStoreProps = (storeId) => storeMap?.[`${storeId}`] ?? {};

  const toNumber = (value) => Number(value) || 0;

  const getDiscountedPrice = (price, discount) => {
    const sellingPrice = toNumber(price);
    const discountRate = toNumber(discount);
    return sellingPrice - (sellingPrice * discountRate) / 100;
  };

  const getProfit = (price, discount, cost) =>
    (getDiscountedPrice(price, discount) - toNumber(cost)).toFixed(2);

  /* ------------------------------ store select ------------------------------- */
  const handleStoreChange = (stores) => {
    const updatedProperties = stores?.map((store) => {
      const existing = storeProperties?.find(
        (prop) => `${prop.store_id}` === `${store.value}`,
      );

      return (
        existing || {
          ...getDefaultStoreProperties(),
          store_id: `${store.value}`,
        }
      );
    });

    setRootFormDetails((prev) => ({
      ...prev,
      store_properties: updatedProperties,
    }));
  };

  const applyPricingAcrossStores = (sourceStoreIndex) => {
    setRootFormDetails((prev) => {
      const sourceStore = prev.store_properties[sourceStoreIndex];

      if (!sourceStore) return prev;

      const updatedStoreProperties = prev.store_properties.map(
        (store, index) => {
          if (index === sourceStoreIndex) return store;

          return {
            ...JSON.parse(JSON.stringify(sourceStore)),
            store_id: store.store_id,
            returned_from_backend: store.returned_from_backend,
          };
        },
      );

      return {
        ...prev,
        store_properties: updatedStoreProperties,
      };
    });
  };

  useEffect(() => {
    setValue("store_properties", rootFormDetails?.store_properties);
  }, [rootFormDetails?.store_properties]);

  return (
    <section>
      <div className={`${globalStyles.cardWithBorder} light-bg`}>
        <primaryComponents.MultipleSelectFieldV2
          name="store_properties"
          control={control}
          options={storeList}
          isRequired
          giveControl={false}
          value={rootFormDetails?.store_properties?.map((storeProperty) => {
            const store = storeList?.find(
              (store) => `${store?.id}` === `${storeProperty?.store_id}`,
            );

            return {
              label: store?.name,
              value: store?.id,
            };
          })}
          placeholder="Select store..."
          errorMessage={errors?.store_properties}
          idKey="id"
          nameKey="name"
          label="Select Store To Make Service Available At"
          onChange={handleStoreChange}
        />
      </div>

      <secondaryComponents.LoaderHelper classNames="mt-5" isLoading={isLoading}>
        <div
          className={`${globalStyles.cardWithBorder} ${styles.bottomSection} light-bg mt-3`}
        >
          {!storeProperties.length ? (
            <h5 className="text-center my-4 w-100">
              Please select stores to proceed with pricing
            </h5>
          ) : (
            storeProperties?.map((storeProp, storeIndex) => (
              <div
                className={`${globalStyles.cardWithBorder} white`}
                key={storeProp.store_id}
              >
                <h6>{getStoreProps(storeProp.store_id)?.name ?? ""}</h6>

                {/* ---------------------------- cost price ---------------------------- */}
                <div style={{ maxWidth: 250 }}>
                  <primaryComponents.InputFieldV2
                    name={`store_properties.${storeIndex}.cost_price`}
                    control={control}
                    type="number"
                    label={`Cost Price (${getStoreProps(storeProp.store_id)?.currency_symbol})`}
                    placeholder="0"
                    classNames="white"
                    isRequired
                    isDisabled={
                      storeIndex > 0 && rootFormDetails?.use_same_pricing
                    }
                    value={storeProp.cost_price}
                    errorMessage={
                      errors?.store_properties?.[storeIndex]?.cost_price
                    }
                    onKeyUp={() =>
                      errors?.store_properties?.[storeIndex]?.cost_price &&
                      trigger(`store_properties.${storeIndex}.cost_price`)
                    }
                    onChange={({ target: { value } }) => {
                      const updated = [...storeProperties];
                      updated[storeIndex].cost_price = value;

                      setRootFormDetails((prev) => ({
                        ...prev,
                        store_properties: updated,
                      }));
                    }}
                  />
                </div>

                <hr />

                {/* ----------------------------- pricing rows ----------------------------- */}
                {storeProp?.pricings?.map((pricing, pricingIndex) => {
                  const pricingType = pricingTypes?.find(
                    (p) => p?.id === pricing?.pricing_type_id,
                  );

                  return (
                    <div key={pricing.uuid} className={styles.priceSection}>
                      {/* pricing type */}
                      <div>
                        <label>
                          <span className="text-danger">*</span> Price Type
                        </label>

                        <primaryComponents.SelectFieldV2
                          name={`store_properties.${storeIndex}.pricings.${pricingIndex}.pricing_type_id`}
                          control={control}
                          // giveControl={false}
                          value={pricingType?.label || "Select..."}
                          options={pricingTypes}
                          nameKey="label"
                          idKey="id"
                          isDisabled={
                            (storeIndex > 0 &&
                              rootFormDetails?.use_same_pricing) ||
                            pricingIndex === 0
                          }
                          isSearchable={false}
                          errorMessage={
                            errors?.store_properties?.[storeIndex]?.pricings?.[
                              pricingIndex
                            ]?.pricing_type_id
                          }
                          onChange={(selected) => {
                            const updated = [...storeProperties];
                            updated[storeIndex].pricings[
                              pricingIndex
                            ].pricing_type_id = selected;

                            setRootFormDetails((prev) => ({
                              ...prev,
                              store_properties: updated,
                            }));
                          }}
                        />
                      </div>

                      {/* selling price */}
                      <primaryComponents.InputFieldV2
                        name={`store_properties.${storeIndex}.pricings.${pricingIndex}.price`}
                        control={control}
                        type="number"
                        step={0.01}
                        label={`Selling Price (${
                          getStoreProps(storeProp.store_id)?.currency_symbol
                        })`}
                        placeholder="0"
                        classNames="white"
                        isRequired
                        isDisabled={
                          storeIndex > 0 && rootFormDetails?.use_same_pricing
                        }
                        value={pricing.price}
                        errorMessage={
                          errors?.store_properties?.[storeIndex]?.pricings?.[
                            pricingIndex
                          ]?.price
                        }
                        onKeyUp={() =>
                          errors?.store_properties?.[storeIndex]?.pricings?.[
                            pricingIndex
                          ]?.price &&
                          trigger(
                            `store_properties.${storeIndex}.pricings.${pricingIndex}.price`,
                          )
                        }
                        onChange={({ target: { value } }) => {
                          const updated = [...storeProperties];
                          updated[storeIndex].pricings[pricingIndex].price =
                            value;

                          setRootFormDetails((prev) => ({
                            ...prev,
                            store_properties: updated,
                          }));
                        }}
                      />

                      {/* discount */}
                      <primaryComponents.PercentField
                        label={`Selling price discount (${
                          getStoreProps(storeProp.store_id)?.currency_symbol
                        })`}
                        name={`store_properties.${storeIndex}.pricings.${pricingIndex}.discount_rate`}
                        control={control}
                        value={pricing.discount_rate}
                        isDisabled={
                          storeIndex > 0 && rootFormDetails?.use_same_pricing
                        }
                        dividedValue={toNumber(pricing.price)}
                        onChange={({ percentValue }) => {
                          const updated = [...storeProperties];
                          updated[storeIndex].pricings[
                            pricingIndex
                          ].discount_rate = percentValue;

                          setRootFormDetails((prev) => ({
                            ...prev,
                            store_properties: updated,
                          }));
                        }}
                      />

                      {/* discounted price */}
                      <primaryComponents.InputField
                        type="number"
                        classNames="white"
                        isDisabled
                        label={`Discounted selling price (${
                          getStoreProps(storeProp.store_id)?.currency_symbol
                        })`}
                        value={getDiscountedPrice(
                          pricing.price,
                          pricing.discount_rate,
                        )}
                      />

                      {/* profit */}
                      <primaryComponents.InputField
                        type="number"
                        classNames="white"
                        isDisabled
                        label={`Profit (${getStoreProps(storeProp.store_id)?.currency_symbol})`}
                        value={getProfit(
                          pricing.price,
                          pricing.discount_rate,
                          storeProp.cost_price,
                        )}
                      />

                      {pricingIndex > 0 && (
                        <span
                          className="my-auto cursor-pointer"
                          onClick={() => {
                            const updated = [...storeProperties];
                            updated[storeIndex].pricings = updated[
                              storeIndex
                            ].pricings.filter(
                              (_, index) => index !== pricingIndex,
                            );

                            setRootFormDetails((prev) => ({
                              ...prev,
                              store_properties: updated,
                            }));
                          }}
                        >
                          <DeleteIcon />
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* ------------------------- add pricing / reuse ------------------------- */}
                <div className={styles.useAcrossSection}>
                  <a
                    className="purple--text"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const updated = [...storeProperties];

                      updated[storeIndex].pricings.push({
                        uuid: crypto.randomUUID(),
                        pricing_type_id: null,
                        price: "",
                        discount_rate: "",
                      });

                      setRootFormDetails((prev) => ({
                        ...prev,
                        store_properties: updated,
                      }));
                    }}
                  >
                    + Add another price option
                  </a>

                  {rootFormDetails?.store_properties?.length > 1 && (
                    <primaryComponents.Button
                      classNames={"btn btn--outline smallBtn"}
                      onClick={() => applyPricingAcrossStores(storeIndex)}
                      type="button"
                    >
                      Apply this pricing properties across other stores
                    </primaryComponents.Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </secondaryComponents.LoaderHelper>
    </section>
  );
};
